/**
 * Asset-type inference for `record_attachments.asset_type`.
 *
 * The column is NOT NULL with no default, and the value is derived from the
 * asset's MIME type rather than from the field's declared type — an `image`-typed
 * field pointing at a PDF still stores `document`, because the row describes the
 * asset, not the field. Live data agrees, overwhelmingly `image`.
 *
 * The one copy: interfaces import it from `@auger/core/fields`. The returned set
 * is wider than the attachment field subtypes (image/audio/video/document) —
 * `spreadsheet` and `presentation` are reachable, and the column has no CHECK
 * constraint.
 */
export function inferAssetType(mimeType: unknown): string {
  if (!mimeType || typeof mimeType !== "string") return "document"
  if (mimeType.startsWith("image/")) return "image"
  if (mimeType.startsWith("video/")) return "video"
  if (mimeType.startsWith("audio/")) return "audio"
  if (mimeType === "application/pdf") return "document"
  if (mimeType.includes("spreadsheet") || mimeType.includes("excel")) return "spreadsheet"
  if (mimeType.includes("presentation") || mimeType.includes("powerpoint")) return "presentation"
  if (mimeType.includes("word") || mimeType.includes("document")) return "document"
  return "document"
}

/** The library's coarse views; `document` is every file the other three aren't.
 *  An attachment subtype `accepts` one, and the library search filters by one. */
export type AssetView = "image" | "audio" | "video" | "document"

/** Which view a file falls in, by the same prefixes the library search uses. */
export function assetView(mimeType: unknown): AssetView {
  if (typeof mimeType !== "string") return "document"
  const type = mimeType.toLowerCase()
  if (type.startsWith("image/")) return "image"
  if (type.startsWith("audio/")) return "audio"
  if (type.startsWith("video/")) return "video"
  return "document"
}

/** How a file previews and plays: finer than inferAssetType, for picking a viewer. */
export type AssetKind =
  | "image"
  | "video"
  | "audio"
  | "pdf"
  | "spreadsheet"
  | "presentation"
  | "document"
  | "text"
  | "other"

export function assetKind(mimeType: unknown): AssetKind {
  if (!mimeType || typeof mimeType !== "string") return "other"
  if (mimeType.startsWith("image/")) return "image"
  if (mimeType.startsWith("video/")) return "video"
  if (mimeType.startsWith("audio/")) return "audio"
  if (mimeType === "application/pdf") return "pdf"
  if (mimeType.includes("spreadsheet") || mimeType.includes("excel") || mimeType === "text/csv")
    return "spreadsheet"
  if (mimeType.includes("presentation") || mimeType.includes("powerpoint")) return "presentation"
  if (mimeType.includes("word") || mimeType.includes("document")) return "document"
  if (mimeType.startsWith("text/")) return "text"
  return "other"
}
